import { useEffect, useState, useCallback, useRef } from 'react'
import { isCrmCoreRole } from '@crm/shared'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import type { SelectionChangedEvent } from 'ag-grid-community'
import { getRequest } from '../../shared/api/requests.api'
import { PageLoader } from '../../shared/ui/Spinner'
import '../../shared/ui/ag-grid-theme.css'
import { useAuthStore } from '../auth/authStore'
import { OrderEntryPanel } from '../orders/components/OrderEntryPanel'
import { AddItemForm } from './components/AddItemForm'
import HistoryPanel from './components/HistoryPanel'
import type { RequestItem, RequestItemStatus, SourceType } from '../../shared/api/types'
import { EditRequestModal } from './components/EditRequestModal'
import { RequestHeader } from './components/RequestHeader'
import { ArchiveRequestModal } from './components/ArchiveRequestModal'
import { ItemsToolbar } from './components/ItemsToolbar'
import { ItemsGrid } from './components/ItemsGrid'
import { CellQuoteModal, type CellQuoteTarget } from './components/CellQuoteModal'
import { MatrixQuotesPopover, type MatrixQuotesTarget } from './components/MatrixQuotesPopover'
import type { CellAnchor } from './components/priceColDefs'
import { SelectedItemsBar } from './components/SelectedItemsBar'
import { ProcurementSelectedBar } from './components/ProcurementSelectedBar'
import { ItemsActionsRow } from './components/ItemsActionsRow'
import { RequestBottomPanel } from './components/RequestBottomPanel'
import { KPAccordion } from './components/KPAccordion'
import { KPControlPanel } from './components/KPControlPanel'
import { RequestLoadError } from './components/RequestLoadError'
import { ProcurementCompletionPanel } from './components/ProcurementCompletionPanel'
import { NotificationFocusBanner } from './components/NotificationFocusBanner'
import { useColStatePersistence } from './hooks/useColStatePersistence'
import { useKpMode } from './hooks/useKpMode'
import { useRequestActions } from './hooks/useRequestActions'
import { useUploadResponse } from './hooks/useUploadResponse'
import { useRequestRelatedData } from './hooks/useRequestRelatedData'
import { useVisibleItems } from './hooks/useVisibleItems'
import { useItemColDefs } from './hooks/useItemColDefs'
import { useDeleteSelectedItems } from './hooks/useDeleteSelectedItems'
import { useBulkUpdateMatrix } from './hooks/useBulkUpdateMatrix'
import { useBulkAssignProcurement } from './hooks/useBulkAssignProcurement'
import { useSendToLogist } from './hooks/useSendToLogist'
import { useManagerQuotesExcel } from './hooks/useManagerQuotesExcel'
import { useEnterKpMode } from './hooks/useEnterKpMode'
import { useKpCellSelect } from './hooks/useKpCellSelect'
import { toMatrixRates } from './utils/convertQuoteToMatrixField'
import { useNotificationFocus } from './hooks/useNotificationFocus'
import { useAgGridScrollRestoration } from '../../shared/hooks/useScrollRestoration'

ModuleRegistry.registerModules([AllCommunityModule])

// Стабильная пустая ссылка, пока заявка не загружена (отпечаток квот в useKpCellSelect).
const EMPTY_ITEMS: RequestItem[] = []

// С 25.05.2026 добавлять позиции можно во ВСЕХ активных статусах заявки —
// типичный сценарий: клиент после высланного КП докинул ещё один партномер,
// менеджер дописывает позицию в существующую заявку.
// Не разрешаем только закрытые/архив (CLOSED_*/ARCHIVED) — там заявка
// заморожена; и DRAFT — там форма создания, AddItemForm не нужен.
const ITEM_EDITABLE_STATUSES = [
  'NEW',
  'SENT_TO_PROCUREMENT',
  'IN_PROGRESS',
  'PARTIALLY_PROCESSED',
  'QUOTATION_READY',
  'QUOTATION_CREATED',
  'QUOTE_SENT',
]

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const [itemSearch, setItemSearch] = useState('')
  const [itemStatusFilter, setItemStatusFilter] = useState<RequestItemStatus | ''>('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)

  const kp = useKpMode()
  const {
    data: request,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['requests', id],
    // staleTime: 5_000 — окно дедупликации параллельных GET'ов карточки.
    // На WS-инвалидациях через scheduleInvalidate (батч 150мс) и точечных
    // setQueryData мы и так держим данные актуальными, а 22 одинаковых
    // refetch'а карточки за 30 сек (что было в логах) — это уже шторм.
    // refetchOnMount: false — карточка освежается через WS, не по навигации.
    queryFn: () => getRequest(id!),
    enabled: !!id,
    staleTime: 5_000,
    refetchOnMount: false,
    retry: (count, err) => {
      if (axios.isAxiosError(err) && [403, 404].includes(err.response?.status ?? 0)) return false
      return count < 1
    },
  })

  const actions = useRequestActions(request, id)
  const upload = useUploadResponse(request, id)
  const { procUsers, notesMap, exchangeRates, markupConstants, cnyRate, pricing } =
    useRequestRelatedData(request, user?.role === 'PROCUREMENT')

  const gridRef = useRef<AgGridReact>(null)
  // Восстановление скролла таблицы позиций при возврате со страницы позиции.
  const itemsTableRef = useRef<HTMLDivElement>(null)
  useAgGridScrollRestoration(`request-items:${id}`, itemsTableRef)
  const { saveColState, onGridReady, resetColState } = useColStatePersistence(user)
  const { selectedIds, setSelectedIds, isDeleting, handleDeleteSelected, cancelSelection } =
    useDeleteSelectedItems(id, gridRef)
  const bulkMatrix = useBulkUpdateMatrix(id, gridRef, () => setSelectedIds(new Set()))
  const bulkAssign = useBulkAssignProcurement(id, gridRef, () => setSelectedIds(new Set()))
  const sendToLogist = useSendToLogist(id, () => setSelectedIds(new Set()))
  // В колонках заметок уже включён autoHeight. resetRowHeights() при autoHeight
  // в AG Grid v35 даёт warning #3 и не нужен: высота пересчитывается самим grid.
  const handleProcNoteHeight = useCallback(() => {}, [])

  const isAdmin = user?.role === 'ADMIN'
  const isProcurement = user?.role === 'PROCUREMENT'
  const canEditPrices =
    isAdmin || user?.role === 'MANAGER' || user?.role === 'ROP' || user?.role === 'ASSISTANT'

  // Работа с квотами прямо из ячейки матрицы (фича «вводишь цену → модалка»):
  // — quoteModalTarget: открыта модалка создания квоты (ввод цены / «+ Добавить»);
  // — quotesPopoverTarget: открыт поповер-список квот матрицы (клик по бейджу).
  const [quoteModalTarget, setQuoteModalTarget] = useState<CellQuoteTarget | null>(null)
  const [quotesPopoverTarget, setQuotesPopoverTarget] = useState<MatrixQuotesTarget | null>(null)

  const handleCellPriceEntered = useCallback(
    (itemId: string, sourceType: SourceType, matrixLabel: string, price: string) => {
      setQuotesPopoverTarget(null)
      setQuoteModalTarget({ itemId, sourceType, matrixLabel, prefillPrice: price })
    },
    [],
  )
  const handleOpenQuotes = useCallback(
    (itemId: string, sourceType: SourceType, matrixLabel: string, anchor: CellAnchor) => {
      setQuoteModalTarget(null)
      setQuotesPopoverTarget({ itemId, sourceType, matrixLabel, anchor })
    },
    [],
  )

  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent) => {
      const rows = e.api.getSelectedRows() as Array<{ id: string }>
      setSelectedIds(new Set(rows.map((r) => r.id)))
      e.api.refreshCells({ columns: ['select'], force: true })
    },
    [setSelectedIds],
  )

  const { visibleItems, agRowData } = useVisibleItems(
    request,
    isProcurement,
    itemSearch,
    itemStatusFilter,
    procUsers,
    notesMap,
    user?.role,
  )

  useEffect(() => {
    if (!request || request.status !== 'DRAFT' || isFetching) return
    void navigate(`/requests/new?draftId=${request.id}`, { replace: true })
  }, [request, isFetching, navigate])

  useEffect(() => {
    if (selectedIds.size === 0) return
    const visibleIds = new Set(visibleItems.map((item) => item.id))
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((itemId) => visibleIds.has(itemId)))
      if (next.size === prev.size) return prev
      if (next.size === 0) gridRef.current?.api.deselectAll()
      return next
    })
  }, [selectedIds, setSelectedIds, visibleItems])

  const focus = useNotificationFocus(gridRef, agRowData)

  // Клик по цене в режиме КП: поставщик строки — от квоты, чья цена в ячейке (№73).
  const handleSelectCellPrice = useKpCellSelect({
    requestId: id,
    items: request?.items ?? EMPTY_ITEMS,
    kpMode: kp.kpMode,
    quoteRates: toMatrixRates(exchangeRates),
    onSelect: kp.handleSelectPrice,
  })

  const itemColDefs = useItemColDefs({
    request,
    user,
    requestId: id!,
    procUsers,
    handleProcNoteHeight,
    cnyRate,
    markupConstants: markupConstants ?? null,
    pricingReady: pricing.ready,
    kpMode: kp.kpMode,
    selectedPrices: kp.selectedPrices,
    onSelectPrice: handleSelectCellPrice,
    onOpenQuotes: handleOpenQuotes,
  })

  // Excel-инструменты менеджерских квот вынесены в хук (см. useManagerQuotesExcel).
  // Вызывается до early-return — внутри всё через optional chaining.
  const selectedItemsForExcel = (request?.items ?? []).filter((i) => selectedIds.has(i.id))
  const { excelBusy, excelMessage, setExcelMessage, handleDownloadExcel, handleUploadExcel } =
    useManagerQuotesExcel(id, request, selectedItemsForExcel)

  // Вход в режим КП (гейт готовности цен + предзаполнение) — см. useEnterKpMode.
  const handleEnterKpMode = useEnterKpMode({
    requestId: id,
    visibleItems,
    cnyRate,
    markupConstants,
    pricing,
    kp,
  })

  if (isLoading) return <PageLoader />
  if (isError) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    const msg =
      status === 403
        ? 'Нет доступа к этой заявке'
        : status === 404
          ? 'Заявка не найдена'
          : ((error as { message?: string } | null)?.message ?? 'Не удалось загрузить заявку')
    const canRetry = status !== 403 && status !== 404
    return <RequestLoadError message={msg} onRetry={canRetry ? () => void refetch() : undefined} />
  }
  if (!request) return <p className="text-sm text-gray-500">Заявка не найдена</p>
  if (request.status === 'DRAFT') return <PageLoader />

  // Изменить / копировать / архивировать / удалить заявку. Помощник (ASSISTANT)
  // может всё это с 29.09.2026 (обращение помощника 28.09, заказчик одобрил) —
  // сервер ограничивает его заявками прикреплённых менеджеров.
  const canDeleteRequest =
    user?.role === 'ADMIN' ||
    user?.role === 'MANAGER' ||
    user?.role === 'ROP' ||
    user?.role === 'ASSISTANT'
  const canAddItems = canDeleteRequest && ITEM_EDITABLE_STATUSES.includes(request.status)
  const totalItems = request.items?.length ?? 0
  const selectedItems = visibleItems.filter((i) => selectedIds.has(i.id))
  const selectedVisibleIds = selectedItems.map((item) => item.id)
  const canShowBulkBar =
    (isAdmin || user?.role === 'MANAGER' || user?.role === 'ROP' || user?.role === 'ASSISTANT') &&
    selectedVisibleIds.length > 0
  // Блоки КП — нынешним ролям, кроме рядового закупщика (белый список, 23.09.2026).
  const seesKpBlocks = isCrmCoreRole(user?.role ?? '') && user?.role !== 'PROCUREMENT'
  const canShowProcurementBar =
    (user?.role === 'PROCUREMENT' || user?.role === 'ROS') && selectedVisibleIds.length > 0

  return (
    <div className="space-y-4">
      <RequestHeader
        request={request}
        userRole={user?.role}
        canDeleteRequest={canDeleteRequest}
        isCopying={actions.isCopying}
        isDeletingRequest={actions.isDeletingRequest}
        isUnarchiving={actions.isUnarchiving}
        onEdit={() => setShowEditModal(true)}
        onCopy={actions.handleCopyRequest}
        onDelete={actions.handleDeleteRequest}
        onArchive={() => setShowArchiveModal(true)}
        onUnarchive={actions.handleUnarchiveRequest}
      />

      {focus.showBanner && focus.focusItemId && (
        <NotificationFocusBanner itemId={focus.focusItemId} focusKind={focus.focusKind} />
      )}

      <ProcurementCompletionPanel requestId={request.id} />

      <div>
        <div ref={itemsTableRef} className="table-base">
          <ItemsToolbar
            totalItems={totalItems}
            visibleCount={visibleItems.length}
            itemSearch={itemSearch}
            setItemSearch={setItemSearch}
            itemStatusFilter={itemStatusFilter}
            setItemStatusFilter={setItemStatusFilter}
            isAdmin={isAdmin}
            userRole={user?.role}
            bulkPriorityId={actions.bulkPriorityId}
            setBulkPriorityId={actions.setBulkPriorityId}
            isBulkPriority={actions.isBulkPriority}
            onBulkPriority={actions.handleBulkPriority}
            bulkAssignId={actions.bulkAssignId}
            setBulkAssignId={actions.setBulkAssignId}
            isBulkAssigning={actions.isBulkAssigning}
            onBulkAssign={actions.handleBulkAssign}
            gridRef={gridRef}
            onResetCols={resetColState}
          />

          {visibleItems.length === 0 ? (
            <p className="text-sm text-gray-400 px-5 py-4">
              {itemSearch || itemStatusFilter
                ? 'Нет позиций по фильтру'
                : canAddItems
                  ? 'Позиций нет — добавьте первую ниже'
                  : 'Позиций нет'}
            </p>
          ) : (
            <ItemsGrid
              ref={gridRef}
              rowData={agRowData}
              columnDefs={itemColDefs}
              isAdmin={isAdmin}
              userRole={user?.role}
              requestId={id}
              onSelectionChanged={onSelectionChanged}
              onGridReady={onGridReady}
              onSaveColState={saveColState}
              onCellPriceEntered={canEditPrices ? handleCellPriceEntered : undefined}
            />
          )}

          {canShowBulkBar && (
            <SelectedItemsBar
              selectedCount={selectedVisibleIds.length}
              isDeleting={isDeleting}
              isApplyingMatrix={bulkMatrix.isApplying}
              matrixError={bulkMatrix.error}
              onDelete={() => void handleDeleteSelected(selectedVisibleIds)}
              onApplyMatrix={(matrix) => bulkMatrix.apply(selectedVisibleIds, matrix)}
              onAssign={(assignedToId) => void bulkAssign.apply(selectedVisibleIds, assignedToId)}
              isAssigning={bulkAssign.isApplying}
              onSendToLogist={() => void sendToLogist.send(selectedVisibleIds)}
              isSendingToLogist={sendToLogist.isSending}
              onCancel={() => {
                cancelSelection()
                bulkMatrix.clearError()
                bulkAssign.clearError()
                setExcelMessage(null)
              }}
              onDownloadExcel={() => void handleDownloadExcel()}
              onUploadExcel={(file) => void handleUploadExcel(file)}
              isDownloadingExcel={excelBusy === 'down'}
              isUploadingExcel={excelBusy === 'up'}
              excelMessage={excelMessage}
            />
          )}

          {canShowProcurementBar && (
            <ProcurementSelectedBar
              requestId={request.id}
              requestNumber={request.requestNumber}
              clientCode={request.client?.code ?? null}
              selectedItems={selectedItems}
              onClear={cancelSelection}
            />
          )}

          {totalItems > 0 && (
            <ItemsActionsRow
              request={request}
              isProcurement={isProcurement}
              isRos={user?.role === 'ROS'}
              canCreateKp={user?.role !== 'GENERAL_DIRECTOR'}
              visibleItems={visibleItems}
              uploadRef={upload.uploadRef}
              isUploading={upload.isUploading}
              uploadResult={upload.uploadResult}
              onUpload={upload.handleUploadResponse}
              kpMode={kp.kpMode}
              onEnterKp={handleEnterKpMode}
              selectedPrices={kp.selectedPrices}
              setSelectedPrices={kp.setSelectedPrices}
              kpMarkups={kp.kpMarkups}
              setKpMarkups={kp.setKpMarkups}
              positionOverrides={kp.positionOverrides}
              setPositionOverrides={kp.setPositionOverrides}
              showOverrides={kp.showOverrides}
              setShowOverrides={kp.setShowOverrides}
              onCancelKp={kp.handleCancelKp}
              exchangeRates={exchangeRates}
              markupConstants={markupConstants}
              pricing={pricing}
            />
          )}

          {canAddItems && (
            <div className="px-4 py-2.5 border-t border-gray-100">
              <table className="w-full">
                <tbody>
                  <AddItemForm requestId={request.id} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <RequestBottomPanel request={request} userRole={user?.role} />

      {/* «Контроль КП» — внутренние напоминания продажной линии. Рядовому
          закупщику не показываем, РОЗ видит (решение Сергея 19.09.2026:
          руководителю закупки полезно знать, что обещано клиенту). */}
      {seesKpBlocks && (
        <KPControlPanel
          requestId={request.id}
          status={request.status}
          quoteSentAt={request.quoteSentAt}
          canEdit={
            user?.role === 'MANAGER' ||
            user?.role === 'ADMIN' ||
            user?.role === 'ROP' ||
            user?.role === 'ASSISTANT'
          }
        />
      )}

      {seesKpBlocks && <KPAccordion requestId={request.id} />}

      {/* Блок 2: оформление заказа из позиций «Счёт» (выгрузка шаблона счёта в УНФ). */}
      <OrderEntryPanel
        requestId={request.id}
        sellerEntityId={request.sellerEntityId}
        items={request.items ?? []}
        userRole={user?.role}
      />

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="text-sm font-medium text-gray-700 mb-3">История изменений</h2>
        <HistoryPanel requestId={request.id} />
      </div>

      {showEditModal && (
        <EditRequestModal
          request={request}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false)
            void queryClient.invalidateQueries({ queryKey: ['requests', id] })
          }}
        />
      )}

      {showArchiveModal && (
        <ArchiveRequestModal
          requestId={request.id}
          onClose={() => setShowArchiveModal(false)}
          onArchived={() => {
            setShowArchiveModal(false)
            void queryClient.invalidateQueries({ queryKey: ['requests', id] })
            // «Контроль КП»: архивация авто-отменяет напоминание о контакте.
            void queryClient.invalidateQueries({ queryKey: ['follow-ups', id] })
            void queryClient.invalidateQueries({ queryKey: ['follow-up-alerts'] })
          }}
        />
      )}

      {/* Работа с квотами прямо из ячейки матрицы (вводишь цену → модалка). */}
      {quoteModalTarget && (
        <CellQuoteModal
          target={quoteModalTarget}
          requestId={id}
          rates={exchangeRates ?? null}
          onClose={() => setQuoteModalTarget(null)}
        />
      )}

      {quotesPopoverTarget && (
        <MatrixQuotesPopover
          target={quotesPopoverTarget}
          requestId={id}
          rates={exchangeRates ?? null}
          readOnly={!canEditPrices}
          onAddNew={() => {
            const t = quotesPopoverTarget
            setQuotesPopoverTarget(null)
            setQuoteModalTarget({
              itemId: t.itemId,
              sourceType: t.sourceType,
              matrixLabel: t.matrixLabel,
              prefillPrice: '',
            })
          }}
          onClose={() => setQuotesPopoverTarget(null)}
        />
      )}
    </div>
  )
}
